/** Utility functions for task item styles, separated from React hook logic. */
import { getContextColor } from '@/features/tasks/utils/ui/colorUtils';

export const getBorderColor = (
  isWaitingFor,
  allContexts,
  isOverdue = false,
) => {
  if (isWaitingFor) return 'var(--color-state-waiting-for)';
  if (isOverdue) return 'var(--color-semantic-warning)';
  const contextColor =
    allContexts.length > 0 ? getContextColor(allContexts[0]) : null;
  return contextColor || null;
};

export const hasAccentBorder = (isWaitingFor, allContexts, isOverdue = false) =>
  Boolean(getBorderColor(isWaitingFor, allContexts, isOverdue));

export const getDisplayLevel = (indentationType, baseLevel) => {
  switch (indentationType) {
    case 'full':
      return baseLevel;
    case 'detail':
      return Math.max(0, baseLevel - 1);
    case 'none':
    default:
      return 0;
  }
};

export const getCursor = (isOverlay, isSmallScreen, isDragDisabled) => {
  if (isOverlay) return 'grabbing';
  if (isSmallScreen) return 'pointer';
  return isDragDisabled ? 'default' : 'grab';
};

export const getTaskStateFlags = (
  task,
  plannedParentIds,
  waitingForParentIds,
  selectedTaskIds,
  duplicateOrderKeyTaskIds,
) => ({
  isWaitingFor: waitingForParentIds?.has(task?.id),
  isPlanned: plannedParentIds?.has(task?.id),
  isSelected: selectedTaskIds?.includes(task?.id),
  hasConflict: duplicateOrderKeyTaskIds?.has(task?.id),
});

/**
 * The Plan rail can show the same task in Next Action and Unscheduled.
 * Only the list that owns focus should paint the selection.
 * @param {string[]} selectedTaskIds
 * @param {string|null} listId
 * @param {string|null} focusOwnerId
 * @returns {string[]}
 */
export const selectionIdsForList = (selectedTaskIds, listId, focusOwnerId) => {
  if (listId && focusOwnerId && listId !== focusOwnerId) return [];
  return selectedTaskIds || [];
};

const getSelectionState = (isSelected, isListFocused) => {
  if (!isSelected) return [];
  return isListFocused
    ? ['selected-task-item', 'is-focused-task']
    : ['inactive-selection'];
};

const getConditionalClasses = (flags) => {
  const {
    localCompleted,
    isSelected,
    isListFocused,
    isDragging,
    isDropTarget,
    isOverlay,
    isPlanned,
    isWaitingFor,
    hasConflict,
    isFocused,
    rescheduleCount,
    hasAccentBorder: accentBorder,
  } = flags;

  return [
    localCompleted ? 'completed' : null,
    isFocused ? 'is-in-focus-mode' : null,
    isDragging ? 'dragging' : null,
    isDropTarget ? 'drop-target' : null,
    isOverlay ? 'is-overlay' : null,
    ...getSelectionState(isSelected, isListFocused),
    accentBorder ? 'has-accent-border' : null,
    isPlanned ? 'is-planned' : null,
    isWaitingFor ? 'is-waiting-for' : null,
    hasConflict ? 'has-conflict' : null,
    rescheduleCount >= 5
      ? 'reschedule-error'
      : rescheduleCount >= 3
        ? 'reschedule-warning'
        : null,
  ].filter(Boolean);
};

export const buildClassNameList = (view, variant, flags) => {
  const baseClasses = ['task-item', `view-${view}`, `variant-${variant}`];
  const conditionalClasses = getConditionalClasses(flags);
  return [...baseClasses, ...conditionalClasses].join(' ');
};

export const getBaseLevel = (task) => {
  return task?.materializedPath
    ? Math.max(0, task.materializedPath.split('.').length - 2)
    : 0;
};
